import type { ReactElement, ReactNode } from 'react'
import { Children, useEffect, useMemo, useState } from 'react'

import type { SerializedStyles } from '@emotion/react'
import { useTheme } from '@emotion/react'
import { isEmpty } from 'ramda'
import { useFormContext } from 'react-hook-form'

import { ChevronDownIcon, ChevronUpIcon } from '@twistopayments/design-system/icons/inline'

import { IconButton } from '../../../atoms/button'
import type { CheckboxVariant } from '../checkbox'
import { Checkbox } from '../checkbox'

import { CheckboxGroupContext } from './checkbox-group.context'
import * as styles from './checkbox-group.styles'

type Props = {
  children: ReactNode
  className?: string
  id?: string
  name: string
  toggleAllCheckboxLabel: ReactElement
  autoFocus?: boolean
  collapsed?: boolean
  collapseDisabled?: boolean
  checkboxStyles?: SerializedStyles
  ignoredFields?: string[]
  variant?: CheckboxVariant
  onExpand?: () => void
  onToggleAll?: (checked: boolean, fields: string[]) => void
}

export const CheckboxGroup = ({
  children,
  id,
  name,
  toggleAllCheckboxLabel,
  className,
  autoFocus = false,
  collapsed = true,
  collapseDisabled = false,
  checkboxStyles,
  ignoredFields = [],
  variant = 'default',
  onExpand,
  onToggleAll,
}: Props) => {
  const theme = useTheme()

  const [isCollapsed, setIsCollapsed] = useState(collapsed)
  const { setValue, getValues, watch, formState } = useFormContext()
  const errors = formState.errors
  const [fields, setFields] = useState<string[]>([])
  const groupValues: boolean[] = watch(fields)

  const hasErrors =
    !isEmpty(errors) &&
    Object.keys(errors).some((key: string) => fields.includes(key))

  useEffect(() => {
    if (hasErrors) {
      setIsCollapsed(false)
    }
  }, [hasErrors])

  useEffect(() => {
    if (!collapsed) {
      setIsCollapsed(false)
    }
  }, [collapsed])

  const isGroupChecked = !isEmpty(fields) && groupValues.every(Boolean)
  const isIndeterminate = !isGroupChecked && groupValues.some(Boolean)

  useEffect(() => {
    if (isGroupChecked !== getValues(name)) {
      setValue(name, isGroupChecked)
    }
  }, [getValues, isGroupChecked, name, setValue])

  const handleGroupCheckToggle = () => {
    const newValue = !isGroupChecked
    fields.forEach((fieldName) => {
      setValue(fieldName, newValue, { shouldValidate: true })
    })
    onToggleAll?.(newValue, fields)
  }

  const handleCollapseToggle = () => {
    if (isCollapsed) {
      onExpand?.()
    }
    setIsCollapsed((state) => !state)
  }

  const api = useMemo(() => {
    const registerCheckbox = (field: string) => {
      if (!fields.includes(field) && !ignoredFields.includes(field)) {
        setFields((fieldsState) => [...fieldsState, field])
      }
    }

    return {
      registerCheckbox,
    }
  }, [fields, ignoredFields])

  if (Children.toArray(children).length <= 1) {
    return <>{children}</>
  }

  return (
    <div className={className} css={styles.root} data-testid="checkbox-group">
      <div css={styles.toggleAllContainer}>
        <Checkbox
          autoFocus={autoFocus}
          css={checkboxStyles}
          id={id ?? name}
          indeterminate={isIndeterminate}
          label={toggleAllCheckboxLabel}
          labelTypographyProps={{
            fontWeight: 'medium',
          }}
          name={name}
          variant={variant}
          onChange={handleGroupCheckToggle}
        />
        {collapseDisabled ? null : (
          <IconButton
            color="ghost"
            icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
            onClick={handleCollapseToggle}
          />
        )}
      </div>
      <CheckboxGroupContext.Provider value={api}>
        <div css={[styles.checkboxes(theme), isCollapsed && styles.hidden]}>
          {children}
        </div>
      </CheckboxGroupContext.Provider>
    </div>
  )
}
